import { assertUncomputedFromBase, fromBaseRecord, FromBaseInputMissing } from '../janus-engine/compile/from-base-context';
import type { FromBaseRow } from '../janus-engine/compile/from-base-context';
import { constructDefinitionItemsFromBase } from '../janus-engine/port/k1-compile/from-base/definition-source';
import { compileSurfacePictures } from '../janus-engine/compile/from-base-encounter-media';
import { constructInventoryDefinitionVideos } from '../janus-engine/compile/from-base-inventory-media';
import type { InventoryVideoContext } from '../janus-engine/compile/from-base-inventory-media';
import { itemsCol, itemVideos, layerOf, layerListEntryUuid, primaryPictureOf, sourceOf } from '../shared/array-types';
import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
import { ANCESTRY_DEFINITION_CARDS_SCHEMA, ANCESTRY_CARD_SURFACE_SCHEMA, ancestryDefinitionCards } from './ancestry-definition-cards';
import type { AncestryDefinitionCardsResponse, AncestryCardSurfaceResponse } from './ancestry-definition-cards';
import type { CatalogCard, CatalogCardGroup, CatalogResponse } from './types';

export const CHARACTER_DEFINITION_CARDS_SCHEMA = 'raw-character-definition-cards-v2';
export interface CharacterDefinitionCardsResponse {
    schema: typeof CHARACTER_DEFINITION_CARDS_SCHEMA | 'raw-character-definition-cards-v1';
    family: 'subclasses' | 'classes' | 'lineages';
    definition_ids: string[];
    class_order?: FromBaseRow[];
    base: FromBaseRow;
    total: number;
}
const plain = (value: unknown): string => decodeHtmlEntitiesForPlainText(value);
const itemName = (row: unknown): string => plain(itemsCol(row, 'item_name'));
const MACHINIST_CLASS_ID = '44223199-712F-A11F-BADD-EC10813AFBF7';
interface CatalogMediaViewer { id?: string; primaryRole?: string }
const catalogMediaVisible = (row: unknown, type: 'picture' | 'video', viewer?: CatalogMediaViewer): boolean => {
    const read = type === 'picture' ? pictureField0 : videoField0;
    if (Number(read(row, `${type}_private`) ?? 0) === 0) return true;
    if (['employee', 'admin', 'super_admin'].includes(viewer?.primaryRole ?? '')) return true;
    const owner = String(read(row, `${type}_created_by_id`) ?? '');
    return !!owner && !!viewer?.id && owner.toUpperCase() === viewer.id.toUpperCase();
};

/** Project saved card fields through the existing media and source readers.
 * No raw gallery or reference is copied into a card. */
export function characterDefinitionCard(row: FromBaseRow, base: FromBaseRow, cdnUrl?: string,
    viewer: CatalogMediaViewer | undefined = typeof window === 'undefined' ? undefined
        : (window as unknown as { FateTide?: { user?: CatalogMediaViewer } }).FateTide?.user): CatalogCard {
    const id = String(itemsCol(row, 'item_id') ?? '');
    const pictures = Array.isArray(row.pictures)
        ? { item: compileSurfacePictures(row.pictures, id, 'item', cdnUrl) } : row.pictures;
    const picture = primaryPictureOf({ pictures: Object.fromEntries(Object.entries(pictures ?? {}).map(([type, bucket]) =>
        [type, Array.isArray(bucket) ? bucket.filter(entry => catalogMediaVisible(entry, 'picture', viewer)) : bucket])) });
    const ownVideos = itemVideos(row);
    const videos = ownVideos.length || !fromBaseRecord(base.definition_videos) ? ownVideos
        : itemVideos(constructInventoryDefinitionVideos({ items: row.items },
            { ...base.definition_videos as unknown as InventoryVideoContext, cdnUrl: cdnUrl! }));
    const video = videos.find(entry => catalogMediaVisible(entry, 'video', viewer));
    const source = sourceOf(row) ?? sourceOf(constructDefinitionItemsFromBase(row));
    const requiredClass = layerListEntryUuid(layerOf(row, 'requires_class')) || null;
    return {
        item_id: id, item_name: itemName(row), item_url: `/item.php?id=${encodeURIComponent(id)}`,
        picture_url: pictureField0(picture, 'picture_url') || null,
        video_url: videoField0(video, 'video_url') || null,
        video_mime: videoField0(video, 'video_url') ? videoField0(video, 'video_mime_type') || 'video/mp4' : null,
        source: source ? {
            source_id: String(itemsCol(source, 'source_id') ?? '') || null,
            source_name: plain(itemsCol(source, 'source_name')),
            source_abbreviation: plain(itemsCol(source, 'source_abbreviation')),
        } : null,
        icon_id: id, badge_id: requiredClass, requires_class: requiredClass,
    };
}

/** Class inputs preserve the existing public selection and Machinist inclusion.
 * Unrecognized/missing classes stay in Other; no subclass is discarded. */
export function groupCharacterDefinitionCards(cards: CatalogCard[], classes: FromBaseRow[]): CatalogCardGroup[] {
    const names = new Map(classes.map(row => [String(itemsCol(row, 'item_id') ?? ''), itemName(row)]));
    if (!names.has(MACHINIST_CLASS_ID)) names.set(MACHINIST_CLASS_ID, 'Machinist');
    const buckets = new Map<string, CatalogCard[]>();
    for (const card of cards) {
        const key = card.requires_class && names.has(card.requires_class) ? card.requires_class : '';
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key)!.push(card);
    }
    const groups: CatalogCardGroup[] = [];
    for (const [id, label] of [...names].sort((a, b) => a[1].localeCompare(b[1], undefined, { sensitivity: 'base' }))) {
        const members = buckets.get(id);
        if (members?.length) groups.push({ label, icon_id: id, cards: members });
    }
    const other = buckets.get('');
    if (other?.length) groups.push({ label: 'Other', icon_id: null, cards: other });
    for (const group of groups) group.cards.sort((a, b) => a.item_name.localeCompare(b.item_name, undefined, { sensitivity: 'base' }));
    return groups;
}

/** Raw character families share the saved-field card boundary. */
export async function adoptCharacterDefinitionCards(input: CatalogResponse | CharacterDefinitionCardsResponse | AncestryDefinitionCardsResponse | AncestryCardSurfaceResponse): Promise<CatalogResponse> {
    if ('schema' in input && (input.schema === ANCESTRY_DEFINITION_CARDS_SCHEMA || input.schema === ANCESTRY_CARD_SURFACE_SCHEMA))
        return ancestryDefinitionCards(input, characterDefinitionCard);
    if (!('schema' in input) || ![CHARACTER_DEFINITION_CARDS_SCHEMA, 'raw-character-definition-cards-v1'].includes(input.schema))
        return input as CatalogResponse;
    if (!['subclasses', 'classes', 'lineages'].includes(input.family) || !Array.isArray(input.definition_ids)
        || (input.family === 'subclasses' && !Array.isArray(input.class_order))
        || input.total !== input.definition_ids.length) throw new Error('catalog-character-definition-schema');
    if (!input.total) return { sections: { standard: [], advanced: [] }, groups: [], total: 0 };
    assertUncomputedFromBase(input.base);
    const base = input.base;
    const cdnUrl = typeof window === 'undefined' ? undefined : (window as unknown as { CDN_URL?: string }).CDN_URL;
    const catalog = fromBaseRecord(base.catalog) ? base.catalog : {};
    const cards: CatalogCard[] = [];
    for (const id of input.definition_ids) {
        const saved = catalog[id] ?? catalog[id.toUpperCase()];
        if (!fromBaseRecord(saved)) throw new FromBaseInputMissing('root-definition', id);
        const card = characterDefinitionCard(saved, base, cdnUrl);
        if (input.family !== 'subclasses') {
            delete card.requires_class;
            delete card.badge_id;
            if (input.family === 'lineages') delete card.icon_id;
        }
        cards.push(card);
    }
    return input.family === 'subclasses'
        ? { sections: { standard: [], advanced: [] }, groups: groupCharacterDefinitionCards(cards, input.class_order!), total: cards.length }
        : { sections: { standard: cards, advanced: [] }, total: cards.length };
}
